import React, { useState } from "react";
import RenderAnswer from "./pages/RenderAnswer";
import { FaEdit, FaTrash } from "react-icons/fa";

function InterviewTable({ items, onEdit, onDelete, onHide, db, editId }) {
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const toggleQuestion = (itemId, qIndex) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [qIndex]: !prev[itemId]?.[qIndex] },
    }));
  };

  const collectAnswerBlock = (lines, startIdx) => {
    const block = [];
    for (let i = startIdx; i < lines.length; i++) {
      block.push(lines[i]);
      if (/([=~\-$])\1{3,}$/.test(lines[i].trim())) break;
    }
    return block.join("\n");
  };

  const parseQuestions = (lines) => {
    const questions = [];
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].trim().startsWith("Q)")) {
        questions.push({ qIndex: i, qLine: lines[i], aStartIdx: null });
      } else if (lines[i].trim().startsWith("A)") && questions.length > 0) {
        const last = questions[questions.length - 1];
        if (last.aStartIdx === null) last.aStartIdx = i;
      }
    }
    return questions;
  };

  // ✅ Render rich blocks (text + images) inline
  const renderBlocks = (blocks) => {
    if (!blocks || blocks.length === 0) return null;
    return blocks.map((block, idx) => {
      if (block.type === "image") {
        return (
          <div key={idx} style={{ margin: "10px 0" }}>
            <img
              src={block.value}
              alt={`img-${idx}`}
              onClick={() => setLightboxSrc(block.value)}
              style={{
                maxWidth: "100%",
                maxHeight: 300,
                objectFit: "contain",
                borderRadius: 7,
                border: "1px solid #e0e7ff",
                cursor: "zoom-in",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                display: "block",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "scale(1.01)";
                e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.18)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
              }}
            />
          </div>
        );
      }
      // text block
      if (block.type === "text" && block.value?.trim()) {
        return (
          <div key={idx} style={{ whiteSpace: "pre-wrap", marginBottom: 4 }}>
            {RenderAnswer(block.value)}
          </div>
        );
      }
      return null;
    });
  };

  let globalQuestionCount = 0;

  return (
    <>
      {/* Lightbox */}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out",
          }}
        >
          <img src={lightboxSrc} alt="full" style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }} />
          <button onClick={() => setLightboxSrc(null)} style={{
            position: "fixed", top: 20, right: 24, background: "rgba(255,255,255,0.15)",
            color: "white", border: "none", borderRadius: "50%",
            width: 36, height: 36, fontSize: 18, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>✕</button>
        </div>
      )}

      <table border="1" style={{ marginTop: "2em", width: "100%" }}>
        <tbody>
          {items.map((item) => {
            if (editId && item.id === editId) {
              return (
                <tr key={item.id}>
                  <td colSpan={2} style={{ textAlign: "center", color: "#888" }}>Editing...</td>
                </tr>
              );
            }

            // ✅ New blocks format
            const hasBlocks = item.blocks && item.blocks.length > 0;

            // ✅ Old format — parse Q)/A) from answer string
            const lines = !hasBlocks && item.answer ? item.answer.split("\n") : [];
            const questions = !hasBlocks ? parseQuestions(lines) : [];

            return (
              <tr key={item.id}>
                <td style={{ padding: "10px" }}>

                  {/* ── NEW FORMAT: blocks with inline images ── */}
                  {hasBlocks && (() => {
                    // Split blocks into Q/A groups by finding Q) markers in text blocks
                    const groups = [];
                    let currentGroup = null;

                    item.blocks.forEach((block, idx) => {
                      if (block.type === "text") {
                        const textLines = block.value.split("\n");
                        let remainingLines = [];

                        textLines.forEach(line => {
                          if (line.trim().startsWith("Q)")) {
                            // Save any accumulated lines to current group
                            if (currentGroup && remainingLines.length > 0) {
                              currentGroup.contentBlocks.push({ type: "text", value: remainingLines.join("\n") });
                              remainingLines = [];
                            }
                            // Start new group
                            currentGroup = { qLine: line, contentBlocks: [] };
                            groups.push(currentGroup);
                          } else {
                            remainingLines.push(line);
                          }
                        });

                        // Push remaining lines into current group
                        if (remainingLines.join("").trim() && currentGroup) {
                          currentGroup.contentBlocks.push({ type: "text", value: remainingLines.join("\n") });
                        } else if (remainingLines.join("").trim() && !currentGroup) {
                          // Text before any Q) — show as plain content
                          if (groups.length === 0) {
                            groups.push({ qLine: null, contentBlocks: [{ type: "text", value: remainingLines.join("\n") }] });
                            currentGroup = groups[0];
                          }
                        }
                      } else if (block.type === "image") {
                        // Image block — add to current group or create a no-Q group
                        if (!currentGroup) {
                          currentGroup = { qLine: null, contentBlocks: [] };
                          groups.push(currentGroup);
                        }
                        currentGroup.contentBlocks.push(block);
                      }
                    });

                    return groups.map((group, gIdx) => {
                      if (group.qLine) {
                        globalQuestionCount++;
                        const localCount = globalQuestionCount;
                        const isExpanded = expandedQuestions[item.id]?.[gIdx] || false;
                        return (
                          <div key={gIdx}>
                            <div
                              onClick={() => toggleQuestion(item.id, gIdx)}
                              style={{ cursor: "pointer", fontWeight: "bold", color: isExpanded ? "#0073e6" : "#444", marginBottom: "0.3em" }}
                            >
                              {`${localCount}) ${group.qLine.replace(/^Q\)\s*/, "")}`}
                            </div>
                            {isExpanded && (
                              <div style={{ marginLeft: "1em", color: "green", marginBottom: "0.5em" }}>
                                {renderBlocks(group.contentBlocks)}
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        // No question — just show content always (plain blocks)
                        return (
                          <div key={gIdx} style={{ marginBottom: 8 }}>
                            {renderBlocks(group.contentBlocks)}
                          </div>
                        );
                      }
                    });
                  })()}

                  {/* ── OLD FORMAT: answer string + screenshots array ── */}
                  {!hasBlocks && (
                    <>
                      {questions.map(({ qIndex, qLine, aStartIdx }) => {
                        globalQuestionCount++;
                        const localCount = globalQuestionCount;
                        const isExpanded = expandedQuestions[item.id]?.[qIndex] || false;
                        return (
                          <div key={qIndex}>
                            <div
                              onClick={() => toggleQuestion(item.id, qIndex)}
                              style={{ cursor: "pointer", fontWeight: "bold", color: isExpanded ? "#0073e6" : "#444", marginBottom: "0.3em" }}
                            >
                              {`${localCount}) ${qLine.replace(/^Q\)\s*/, "")}`}
                            </div>
                            {isExpanded && aStartIdx !== null && (
                              <div style={{ marginLeft: "1em", color: "green", marginBottom: "0.5em", whiteSpace: "pre-wrap" }}>
                                {RenderAnswer(collectAnswerBlock(lines, aStartIdx))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Old screenshots */}
                      {item.screenshots?.length > 0 && (
                        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                          {item.screenshots.map((src, sIdx) => (
                            <img key={sIdx} src={src} alt={`ss-${sIdx}`} onClick={() => setLightboxSrc(src)}
                              style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 7, border: "1px solid #e0e7ff", cursor: "zoom-in" }} />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </td>

                <td style={{ padding: "10px", textAlign: "center", whiteSpace: "nowrap" }}>
                  <button type="button" onClick={() => onEdit(item)} title="Edit"
                    style={{ marginRight: "10px", cursor: "pointer", background: "none", border: "none", color: "#007bff" }}>
                    <FaEdit size={18} />
                  </button>
                  <button type="button" onClick={() => onDelete(db, item.id)} title="Delete"
                    style={{ cursor: "pointer", background: "none", border: "none", color: "#dc3545" }}>
                    <FaTrash size={18} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

export default InterviewTable;
